import {
  z,
  ARABIC_LATIN_COMPANY_NAME_REGEX,
  ARABIC_LATIN_CITY_REGEX,
} from '@tasama/utils';
import type { CompanyTypeFormData } from '../stores/company-type-store';

// Company type constants
export const COMPANY_TYPES = {
  INTERNATIONAL: 'INTERNATIONAL',
  KSA_PRIVATE: 'KSA_PRIVATE',
  PIF_ESTABLISHED: 'PIF_ESTABLISHED',
  PIF_OWNED: 'pif-owned',
} as const;

export const COMPANY_STATUS = {
  PIF_ESTABLISHED: 'PIF_ESTABLISHED',
} as const;

// Dropdown options
export const INDUSTRY_SECTORS = [
  {
    value: 'technology',
    label: 'Technology',
    labelKey: 'companyDetails.fields.industrySector.options.technology',
  },
  {
    value: 'healthcare',
    label: 'Healthcare',
    labelKey: 'companyDetails.fields.industrySector.options.healthcare',
  },
  {
    value: 'finance',
    label: 'Finance',
    labelKey: 'companyDetails.fields.industrySector.options.finance',
  },
  {
    value: 'education',
    label: 'Education',
    labelKey: 'companyDetails.fields.industrySector.options.education',
  },
  {
    value: 'manufacturing',
    label: 'Manufacturing',
    labelKey: 'companyDetails.fields.industrySector.options.manufacturing',
  },
  {
    value: 'retail',
    label: 'Retail',
    labelKey: 'companyDetails.fields.industrySector.options.retail',
  },
  {
    value: 'energy',
    label: 'Energy',
    labelKey: 'companyDetails.fields.industrySector.options.energy',
  },
  {
    value: 'real-estate',
    label: 'Real Estate',
    labelKey: 'companyDetails.fields.industrySector.options.realEstate',
  },
  {
    value: 'transportation',
    label: 'Transportation',
    labelKey: 'companyDetails.fields.industrySector.options.transportation',
  },
  {
    value: 'telecommunications',
    label: 'Telecommunications',
    labelKey: 'companyDetails.fields.industrySector.options.telecommunications',
  },
  {
    value: 'agriculture',
    label: 'Agriculture',
    labelKey: 'companyDetails.fields.industrySector.options.agriculture',
  },
  {
    value: 'construction',
    label: 'Construction',
    labelKey: 'companyDetails.fields.industrySector.options.construction',
  },
  {
    value: 'entertainment',
    label: 'Entertainment',
    labelKey: 'companyDetails.fields.industrySector.options.entertainment',
  },
  {
    value: 'hospitality',
    label: 'Hospitality',
    labelKey: 'companyDetails.fields.industrySector.options.hospitality',
  },
  {
    value: 'consulting',
    label: 'Consulting',
    labelKey: 'companyDetails.fields.industrySector.options.consulting',
  },
  {
    value: 'other',
    label: 'Other',
    labelKey: 'companyDetails.fields.industrySector.options.other',
  },
];

export const getIndustrySectorOptions = (t: (_key: string) => string) =>
  INDUSTRY_SECTORS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

export const EMPLOYEE_COUNT_OPTIONS = [
  {
    value: '0-5',
    label: '0 - 5',
    labelKey: 'common:companyForm.employeeCountOptions.range_0_5',
  },
  {
    value: '6-25',
    label: '6 - 25',
    labelKey: 'common:companyForm.employeeCountOptions.range_6_25',
  },
  {
    value: '26-100',
    label: '26 - 100',
    labelKey: 'common:companyForm.employeeCountOptions.range_26_100',
  },
  {
    value: '101-200',
    label: '101 - 200',
    labelKey: 'common:companyForm.employeeCountOptions.range_101_200',
  },
  {
    value: '201-1000',
    label: '201 - 1,000',
    labelKey: 'common:companyForm.employeeCountOptions.range_201_1000',
  },
  {
    value: '>1000',
    label: '> 1,000',
    labelKey: 'common:companyForm.employeeCountOptions.range_gt_1000',
  },
];

export const ANNUAL_REVENUE_OPTIONS = [
  {
    value: '0-1M',
    label: '0 - 1M SAR',
    labelKey: 'common:companyForm.annualRevenueOptions.range_0_1M',
  },
  {
    value: '1M-5M',
    label: '1M SAR - 5M SAR',
    labelKey: 'common:companyForm.annualRevenueOptions.range_1M_5M',
  },
  {
    value: '5M-20M',
    label: '5M SAR - 20M SAR',
    labelKey: 'common:companyForm.annualRevenueOptions.range_5M_20M',
  },
  {
    value: '>20M',
    label: '> 20M SAR',
    labelKey: 'common:companyForm.annualRevenueOptions.range_gt_20M',
  },
];

export const INVESTMENT_RAISED_OPTIONS = [
  {
    value: '0-5M SAR',
    label: '0 - 5M SAR',
    labelKey: 'common:companyForm.investmentRaisedOptions.range_0_5M',
  },
  {
    value: '5M-20M SAR',
    label: '5M SAR - 20M SAR',
    labelKey: 'common:companyForm.investmentRaisedOptions.range_5M_20M',
  },
  {
    value: '20M-50M SAR',
    label: '20M SAR - 50M SAR',
    labelKey: 'common:companyForm.investmentRaisedOptions.range_20M_50M',
  },
  {
    value: '50M-100M SAR',
    label: '50M SAR - 100M SAR',
    labelKey: 'common:companyForm.investmentRaisedOptions.range_50M_100M',
  },
  {
    value: '>100M SAR',
    label: '> 100M SAR',
    labelKey: 'common:companyForm.investmentRaisedOptions.range_gt_100M',
  },
];

export const getEmployeeCountOptions = (t: (_key: string) => string) =>
  EMPLOYEE_COUNT_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));

export const getAnnualRevenueOptions = (t: (_key: string) => string) =>
  ANNUAL_REVENUE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));

export const getInvestmentRaisedOptions = (t: (_key: string) => string) =>
  INVESTMENT_RAISED_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));

// Countries list for international companies
export const COUNTRIES = [
  { value: 'Afghanistan', label: 'Afghanistan' },
  { value: 'Albania', label: 'Albania' },
  { value: 'Algeria', label: 'Algeria' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Austria', label: 'Austria' },
  { value: 'Bahrain', label: 'Bahrain' },
  { value: 'Bangladesh', label: 'Bangladesh' },
  { value: 'Belgium', label: 'Belgium' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Canada', label: 'Canada' },
  { value: 'China', label: 'China' },
  { value: 'Egypt', label: 'Egypt' },
  { value: 'France', label: 'France' },
  { value: 'Germany', label: 'Germany' },
  { value: 'India', label: 'India' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Iran', label: 'Iran' },
  { value: 'Iraq', label: 'Iraq' },
  { value: 'Italy', label: 'Italy' },
  { value: 'Japan', label: 'Japan' },
  { value: 'Jordan', label: 'Jordan' },
  { value: 'Kuwait', label: 'Kuwait' },
  { value: 'Lebanon', label: 'Lebanon' },
  { value: 'Malaysia', label: 'Malaysia' },
  { value: 'Morocco', label: 'Morocco' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Nigeria', label: 'Nigeria' },
  { value: 'Oman', label: 'Oman' },
  { value: 'Pakistan', label: 'Pakistan' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'South Korea', label: 'South Korea' },
  { value: 'Spain', label: 'Spain' },
  { value: 'Switzerland', label: 'Switzerland' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'United States', label: 'United States' },
  { value: 'Yemen', label: 'Yemen' },
];

// GCC countries for local companies
export const GCC_COUNTRIES = [
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'Kuwait', label: 'Kuwait' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'Bahrain', label: 'Bahrain' },
  { value: 'Oman', label: 'Oman' },
];

export const getGccCountriesOptions = (t: (_key: string) => string) =>
  GCC_COUNTRIES.map((c) => ({
    value: c.value,
    label: t(`myAccount.companyDetails.gccCountries.${c.value}`),
  }));

export const getCountriesOptions = (t: (_key: string) => string) =>
  COUNTRIES.map((c) => ({
    value: c.value,
    label: t(`myAccount.companyDetails.countries.${c.value}`),
  }));

// Helper function to get field requirements based on company type
export const getFieldRequirements = (companyTypeData?: CompanyTypeFormData) => {
  const companyType = companyTypeData?.companyType;
  const companyStatus = companyTypeData?.companyStatus;

  // For INTERNATIONAL companies
  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    return {
      companyName: { required: true },
      crNumber: { required: true },
      registrationStartDate: { required: true },
      website: { required: false },
      industrySector: { required: true },
      countryOfHeadquarter: { required: true },
      address: { required: true },
      city: { required: true },
      postalCode: { required: false },
      numberOfEmployees: { required: true },
      annualRevenue: { required: true },
      totalInvestmentRaised: { required: true },
    };
  }

  // For KSA_PRIVATE companies
  if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
    return {
      companyName: { required: true },
      crNumber: { required: true },
      registrationStartDate: { required: true },
      website: { required: false },
      industrySector: { required: true },
      country: { required: true }, // Country of registration (disabled, prefilled with Saudi Arabia)
      address: { required: false },
      city: { required: false },
      postalCode: { required: false },
      numberOfEmployees: { required: false },
      annualRevenue: { required: false },
    };
  }

  // For PIF_ESTABLISHED companies (existing behavior)
  if (
    companyType === COMPANY_TYPES.PIF_OWNED &&
    companyStatus === COMPANY_STATUS.PIF_ESTABLISHED
  ) {
    return {
      companyName: { required: true },
      crNumber: { required: true },
      registrationStartDate: { required: true },
      website: { required: false },
      industrySector: { required: true },
      country: { required: true }, // Country of registration (disabled, prefilled with Saudi Arabia)
      address: { required: false },
      city: { required: false },
      postalCode: { required: false },
    };
  }

  // Default (PIF_ESTABLISHED behavior)
  return {
    companyName: { required: true },
    crNumber: { required: true },
    registrationStartDate: { required: true },
    website: { required: false },
    country: { required: true },
    address: { required: false },
    city: { required: false },
    postalCode: { required: false },
  };
};

// 1. Validation messages factory
const getValidationMessages = (t: (_key: string) => string) => ({
  companyName: {
    required: t('validation.companyDetails.companyName.required'),
    max: t('validation.companyDetails.companyName.max'),
    charset: t('validation.companyDetails.companyName.charset'),
  },
  crNumber: {
    required: t('validation.companyDetails.crNumber.required'),
    max: t('validation.companyDetails.crNumber.max'),
    charset: t('validation.companyDetails.crNumber.charset'),
  },
  registrationStartDate: {
    required: t('validation.companyDetails.registrationStartDate.required'),
    format: t('validation.companyDetails.registrationStartDate.format'),
    future: t('validation.companyDetails.registrationStartDate.future'),
  },
  website: {
    invalid: t('validation.companyDetails.website.invalid'),
  },
  country: {
    required: t('validation.companyDetails.country.required'),
  },
  countryOfHeadquarter: {
    required: t('validation.companyDetails.countryOfHeadquarter.required'),
  },
  city: {
    charset: t('validation.companyDetails.city.charset'),
  },
  postalCode: {
    charset: t('validation.companyDetails.postalCode.charset'),
  },
  industrySector: {
    required: t('validation.companyDetails.industrySector.required'),
  },
  address: {
    required: t('validation.companyDetails.address.required'),
  },
  numberOfEmployees: {
    required: t('validation.companyDetails.numberOfEmployees.required'),
  },
  annualRevenue: {
    required: t('validation.companyDetails.annualRevenue.required'),
  },
  totalInvestmentRaised: {
    required: t('validation.companyDetails.totalInvestmentRaised.required'),
  },
  general: t('validation.companyDetails.general'),
});

// 2. Regex & helpers
// Changed + to * so the regex doesn't fail on an empty string (allowing .min(1) to handle required)
const COMPANY_NAME_REGEX = ARABIC_LATIN_COMPANY_NAME_REGEX;
const REGISTRATION_NUMBER_REGEX = /^[A-Za-z0-9-]*$/;
const CITY_REGEX = ARABIC_LATIN_CITY_REGEX;
const POSTALCODE_REGEX = /^([A-Za-z0-9\s-]{3,12})?$/; // Made optional within regex

// Helper to parse DD/MM/YYYY and check if it's valid
const isValidDateString = (dateStr: string) => {
  // Accepts DD/MM/YYYY format (as provided by DateInput component)
  const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  // Also accepts YYYY-MM-DD or YYYY/MM/DD for backward compatibility
  const yyyymmddRegex = /^(\d{4})[/-](\d{2})[/-](\d{2})$/;

  let year: number, month: number, day: number;

  if (ddmmyyyyRegex.test(dateStr)) {
    // DD/MM/YYYY format
    const match = dateStr.match(ddmmyyyyRegex);
    if (!match) return false;

    day = Number(match[1]);
    month = Number(match[2]);
    year = Number(match[3]);
  } else if (yyyymmddRegex.test(dateStr)) {
    // YYYY-MM-DD or YYYY/MM/DD format
    const match = dateStr.match(yyyymmddRegex);
    if (!match) return false;

    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
  } else {
    return false;
  }

  const date = new Date(year, month - 1, day);

  // Validate logical date (e.g., prevents 31/02/2025)
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

// 4. Dynamic schema based on company type
export const getCompanyDetailsSchema = (
  companyTypeData: CompanyTypeFormData | undefined,
  t: (_key: string) => string
) => {
  const vm = getValidationMessages(t);
  const requirements = getFieldRequirements(companyTypeData);
  const companyType = companyTypeData?.companyType;

  const baseSchema = {
    // COMPANY NAME: Always required
    companyName: z
      .string()
      .trim()
      .min(1, vm.companyName.required)
      .max(200, vm.companyName.max)
      .regex(COMPANY_NAME_REGEX, vm.companyName.charset),

    // REGISTRATION NUMBER: Always required
    crNumber: z
      .string()
      .trim()
      .min(1, vm.crNumber.required)
      .max(100, vm.crNumber.max)
      .regex(REGISTRATION_NUMBER_REGEX, vm.crNumber.charset),

    // REGISTRATION DATE: Always required
    registrationStartDate: z
      .string()
      .trim()
      .min(1, vm.registrationStartDate.required)
      .refine((val) => isValidDateString(val), vm.registrationStartDate.format)
      .refine(
        (val) => {
          let year: number, month: number, day: number;

          // Handle DD/MM/YYYY format
          const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
          if (ddmmyyyyRegex.test(val)) {
            const match = val.match(ddmmyyyyRegex);
            if (!match) return false;
            day = Number(match[1]);
            month = Number(match[2]);
            year = Number(match[3]);
          } else {
            // Handle YYYY-MM-DD or YYYY/MM/DD format
            const parts = val.split(/[-/]/).map(Number);
            if (parts.length !== 3) return false;
            [year, month, day] = parts as [number, number, number];
          }

          const inputDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return inputDate <= today;
        },
        { error: vm.registrationStartDate.future }
      ),

    // WEBSITE: Always optional
    website: z
      .string()
      .trim()
      .refine(
        (val) => val === '' || /^https?:\/\//i.test(val),
        vm.website.invalid
      ),
  };

  // Add conditional fields based on company type
  const conditionalFields: any = {};

  // Industry Sector - required for INTERNATIONAL and KSA_PRIVATE
  if (requirements.industrySector) {
    conditionalFields.industrySector = requirements.industrySector.required
      ? z.string().min(1, vm.industrySector.required)
      : z.string().optional().or(z.literal(''));
  }

  // Country fields
  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    // For international companies, use "Country of headquarters"
    conditionalFields.countryOfHeadquarter = z
      .string()
      .min(1, vm.countryOfHeadquarter.required);
  } else {
    // For KSA companies, use "Country of registration" (disabled, prefilled)
    conditionalFields.country = z.string().min(1, vm.country.required);
  }

  // Address fields
  if (requirements.address) {
    conditionalFields.address = requirements.address.required
      ? z.string().trim().min(1, vm.address.required)
      : z.string().trim();
  }

  if (requirements.city) {
    conditionalFields.city = requirements.city.required
      ? z
          .string()
          .trim()
          .min(1, vm.city.charset)
          .regex(CITY_REGEX, vm.city.charset)
      : z
          .string()
          .trim()
          .regex(CITY_REGEX, vm.city.charset)
          .optional()
          .or(z.literal(''));
  }

  if (requirements.postalCode !== undefined) {
    conditionalFields.postalCode = z
      .string()
      .trim()
      .regex(POSTALCODE_REGEX, vm.postalCode.charset)
      .optional()
      .or(z.literal(''));
  }

  // Additional fields for INTERNATIONAL companies
  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    conditionalFields.numberOfEmployees = z
      .string()
      .min(1, vm.numberOfEmployees.required);
    conditionalFields.annualRevenue = z
      .string()
      .min(1, vm.annualRevenue.required);
    conditionalFields.totalInvestmentRaised = z
      .string()
      .min(1, vm.totalInvestmentRaised.required);
  }

  // Additional optional fields for KSA_PRIVATE companies
  if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
    conditionalFields.numberOfEmployees = z
      .string()
      .optional()
      .or(z.literal(''));
    conditionalFields.annualRevenue = z.string().optional().or(z.literal(''));
  }

  return z.object({ ...baseSchema, ...conditionalFields });
};

// Main schema for backward compatibility (PIF_ESTABLISHED)
export const getCompanyDetailsBaseSchema = (t: (_key: string) => string) => {
  const vm = getValidationMessages(t);
  return z.object({
    // COMPANY NAME: Required
    companyName: z
      .string()
      .trim()
      .min(1, vm.companyName.required)
      .max(200, vm.companyName.max)
      .regex(COMPANY_NAME_REGEX, vm.companyName.charset),

    // REGISTRATION NUMBER: Required
    crNumber: z
      .string()
      .trim()
      .min(1, vm.crNumber.required)
      .max(100, vm.crNumber.max)
      .regex(REGISTRATION_NUMBER_REGEX, vm.crNumber.charset),

    // REGISTRATION DATE: Required + Format + Future check
    registrationStartDate: z
      .string()
      .trim()
      .min(1, vm.registrationStartDate.required)
      .refine((val) => isValidDateString(val), vm.registrationStartDate.format)
      .refine(
        (val) => {
          let year: number, month: number, day: number;

          // Handle DD/MM/YYYY format
          const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
          if (ddmmyyyyRegex.test(val)) {
            const match = val.match(ddmmyyyyRegex);
            if (!match) return false;
            day = Number(match[1]);
            month = Number(match[2]);
            year = Number(match[3]);
          } else {
            // Handle YYYY-MM-DD or YYYY/MM/DD format
            const parts = val.split(/[-/]/).map(Number);
            if (parts.length !== 3) return false;
            [year, month, day] = parts as [number, number, number];
          }

          const inputDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return inputDate <= today;
        },
        { error: vm.registrationStartDate.future }
      ),

    website: z
      .string()
      .trim()
      .refine(
        (val) => val === '' || /^https?:\/\//i.test(val),
        vm.website.invalid
      ),

    country: z.string().min(1, vm.country.required),

    address: z.string().trim(),

    city: z
      .string()
      .trim()
      .regex(CITY_REGEX, vm.city.charset)
      .optional()
      .or(z.literal('')),

    postalCode: z
      .string()
      .trim()
      .regex(POSTALCODE_REGEX, vm.postalCode.charset)
      .optional()
      .or(z.literal('')),
  });
};

// Extended type to include all possible fields across company types
export interface ExtendedCompanyDetailsFormValues {
  companyName: string;
  crNumber: string;
  registrationStartDate: string;
  establishedDate: string;
  website?: string | undefined;
  industrySector?: string | undefined;
  country?: string | undefined; // For KSA companies (country of registration)
  countryOfHeadquarter?: string | undefined; // For international companies
  address?: string | undefined;
  city?: string | undefined;
  postalCode?: string | undefined;
  numberOfEmployees?: string | undefined;
  annualRevenue?: string | undefined;
  totalInvestmentRaised?: string | undefined;
  companyType?: string | undefined;
}

export type CompanyDetailsFormValues = ExtendedCompanyDetailsFormValues;

// Type export for the field validation function
export type CompanyDetailsFieldName = keyof ExtendedCompanyDetailsFormValues;

// Dynamic default values based on company type
export const getCompanyDetailsDefaultValues = (
  companyTypeData?: CompanyTypeFormData
): Partial<ExtendedCompanyDetailsFormValues> => {
  const companyType = companyTypeData?.companyType;

  const baseDefaults = {
    companyName: '',
    crNumber: '',
    registrationStartDate: '',
    website: '',
    address: '',
    city: '',
    postalCode: '',
  };

  if (companyType === COMPANY_TYPES.INTERNATIONAL) {
    return {
      ...baseDefaults,
      industrySector: '',
      countryOfHeadquarter: '',
      numberOfEmployees: '',
      annualRevenue: '',
      totalInvestmentRaised: '',
    };
  }

  if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
    return {
      ...baseDefaults,
      industrySector: '',
      country: 'Saudi Arabia',
      numberOfEmployees: '',
      annualRevenue: '',
    };
  }

  // Default for PIF_ESTABLISHED and others
  return {
    ...baseDefaults,
    country: 'Saudi Arabia',
  };
};

export const companyDetailsDefaultValues: Partial<ExtendedCompanyDetailsFormValues> =
  {
    companyName: '',
    crNumber: '',
    registrationStartDate: '',
    website: '',
    country: 'Saudi Arabia',
    address: '',
    city: '',
    postalCode: '',
  };

// Field validation helper for individual field validation
export const validateCompanyDetailsField = (
  field: keyof ExtendedCompanyDetailsFormValues,
  value: unknown,
  t: (_key: string) => string,
  companyTypeData?: CompanyTypeFormData
): string | null => {
  const vm = getValidationMessages(t);
  try {
    // Create a test object with just this field
    const testData = { [field]: value };
    // Get the appropriate schema based on company type
    const schema = getCompanyDetailsSchema(companyTypeData, t);
    // Pick only the field schema from the main schema
    const fieldSchema = schema.pick({ [field]: true } as any);
    fieldSchema.parse(testData);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldError = error.issues.find((issue: any) =>
        issue.path.includes(field)
      );
      if (fieldError) {
        return fieldError.message;
      }
    }
    return vm.general;
  }
};
